import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { auth } from './config/firebase';
import { signInAnonymously } from 'firebase/auth';
import firebaseService from './services/firebaseService';
import Game from './components/Game';
import LandingPage from './components/LandingPage';
import RoomPreview from './components/RoomPreview';
import BackgroundGallery from './components/BackgroundGallery';
import './styles/components.css';

// Room component that handles the room preview and joining
const Room = ({ onGameStart }) => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  const handleJoin = async (game) => {
    try {
      if (game) {
        onGameStart(game);
      } else {
        setError('Failed to join game: Game not found');
      }
    } catch (error) {
      console.error('Error joining game:', error);
      setError('Failed to join game: ' + error.message);
    }
  };

  return (
    <RoomPreview
      roomId={roomId}
      onJoin={handleJoin}
      error={error}
    />
  );
};

function App() {
  const [gameState, setGameState] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Initialize Firebase Auth
    signInAnonymously(auth).catch((error) => {
      console.error('Error signing in anonymously:', error);
      let errorMessage = 'Failed to initialize';

      switch (error.code) {
        case 'auth/admin-restricted-operation':
          errorMessage = 'Anonymous sign-in is not enabled. Please contact the administrator.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Anonymous sign-in is not enabled. Please contact the administrator.';
          break;
        default:
          errorMessage = error.message;
      }

      setError(errorMessage);
    });

    // Cleanup function
    return () => {
      if (gameState?.roomId) {
        firebaseService.cleanup(gameState.roomId);
      }
    };
  }, []);

  const handleGameStart = (game) => {
    console.log('Starting game:', game);
    if (game && game.roomId) {
      setGameState(game);
      // Update URL when game is created
      window.history.pushState({}, '', `/room/${game.roomId}`);
    } else {
      console.error('Invalid game data received:', game);
      setError('Invalid game data received');
    }
  };

  const handleGameUpdate = (updatedGame) => {
    console.log('Updating game state:', updatedGame);
    if (updatedGame && updatedGame.roomId) {
      setGameState(updatedGame);
    } else {
      console.error('Invalid game update received:', updatedGame);
      setError('Invalid game update received');
    }
  };

  const isPromptGiver = gameState?.promptGiver?.id === auth.currentUser?.uid;

  return (
    <BrowserRouter>
      <div className="app">
        {error && (
          <div className="error-container">
            <div className="error-message">{error}</div>
          </div>
        )}

        {!gameState ? (
          <div className="landing-container">
            <div className="backgorund-pictures"></div>
            <div className="floating-shapes">
              <div className="shape"></div>
              <div className="shape"></div>
              <div className="shape"></div>
            </div>
            <BackgroundGallery />
            <Routes>
              <Route path="/" element={<LandingPage onGameStart={handleGameStart} />} />
              <Route path="/room/:roomId" element={<Room onGameStart={handleGameStart} />} />
            </Routes>
          </div>
        ) : (
          <Game
            gameState={{
              ...gameState,
              onGameUpdate: handleGameUpdate
            }}
            isPromptGiver={isPromptGiver}
          />
        )}
      </div>
    </BrowserRouter>
  );
}

export default App;
